import {
  ConversationTypeEnum,
  PendingEndlink,
  ReplyOption
} from "@coxauto-ui/cxm/communications/interfaces";
import { useCallback, useContext, useMemo, useRef } from "react";
import styled from "styled-components";
import {
  ConversationContext,
  ConversationDetailsContext,
  useTextReply
} from "../../context";
import MessageSendButton from "../message-send-button";
import { TemplateSelectorModal } from "../template-selector-modal";
import { MessageInput } from "../message-input";
import { ChannelValueSelector } from "../channel-value-selector";
import MessageAttachmentUpload from "../message-attachment-upload";
import { StationeryOptions } from "@coxauto-ui/cxm/communications/api";
import { TextMessageEmojiButton } from "../emoji-picker/text-message-emoji-button";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import { PlusButton } from "../components/plus-button";
import { useTemplates } from "../hooks/useTemplates";
import { usePlusButtonMenuItems } from "../hooks/usePlusButtonMenuItems";
import {
  MediaEndlinkInsert,
  useMediaLibraryPicker
} from "../hooks/useMediaLibraryPicker";
import { MediaLibraryModal } from "../media-library-modal";
import { useInsertAtCursor } from "../hooks/useInsertAtCursor";
import { useTextOptInStatus } from "../hooks/useTextOptInStatus";
import { ReplyAlertsBottomContainer } from "../reply-alerts-bottom-container";
import { ReplyAlertsTopContainer } from "../reply-alerts-top-container";
import { LinkPreviewCard } from "../../components/link-preview-card";

interface TextReplyProps {
  stationeryOptions: StationeryOptions;
  selectedReplyOption: ReplyOption | null;
  messageFailedToSend: boolean;
  messageSentResponseFailedMsg: string;
  isMediaEntitled: boolean;
  isTextAndNoPhoneNumber: boolean;
  isTextWithRepairOrderOnly: boolean;
  onMessageSendFailed: () => void;
  onMessageSendFailedSetMsg: (msg: string) => void;
}

const StyledReplyBottom = styled.div`
  margin-top: 0.5em;
  display: flex;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const StyledLeftButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledChipRail = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

function makeChipId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `chip-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function TextReply({
  stationeryOptions,
  selectedReplyOption,
  messageFailedToSend,
  messageSentResponseFailedMsg,
  isMediaEntitled,
  isTextAndNoPhoneNumber,
  isTextWithRepairOrderOnly,
  onMessageSendFailed,
  onMessageSendFailedSetMsg
}: TextReplyProps) {
  const {
    "nx.cxm.show-emoji-text": showTextEmojiPicker,
    "nx.cxm.inbox-show-plus-button": showPlusButton,
    "nx.cxm.inbox-show-media-library": showMediaLibraryFlag
  } = useCxmFlags();

  const {
    autoLeadId,
    repairOrderId,
    autoLeadStatus,
    leadStatusError,
    dealerCommunicationDetails,
    customerId,
    customerSystem,
    contactDetails,
    env,
    refreshContactDetails
  } = useContext(ConversationContext);
  const {
    replyText,
    setReplyText,
    selectedToChannelValue,
    selectedTemplateLoadState,
    setSelectedTemplateLoadState,
    isTextOptInPending,
    setIsTextOptInPending,
    isPhoneValidationBlocking,
    selectedMedia,
    setSelectedMedia,
    pendingEndlinks,
    setPendingEndlinks
  } = useTextReply();

  const removeEndlink = useCallback(
    (chipId: string) => {
      setPendingEndlinks(prev => prev.filter(e => e.chipId !== chipId));
    },
    [setPendingEndlinks]
  );

  const selectedContactConsent = useMemo(() => {
    if (!contactDetails || !selectedToChannelValue) return null;
    const textContacts =
      contactDetails.contactChannels[ConversationTypeEnum.TEXT] ?? [];
    return (
      textContacts.find(
        c =>
          c.formattedValue === selectedToChannelValue ||
          c.value === selectedToChannelValue
      )?.consent ?? null
    );
  }, [contactDetails, selectedToChannelValue]);

  useTextOptInStatus({
    env,
    customerId,
    customerSystem,
    selectedToChannelValue,
    canSend: selectedContactConsent?.canSend ?? false,
    optInStatus: selectedContactConsent?.optInStatus ?? null,
    ebrStatus: selectedContactConsent?.ebrStatus ?? null,
    setIsTextOptInPending,
    refreshContactDetails
  });
  const { emojiPickerError } = useContext(ConversationDetailsContext);

  const {
    templateData,
    isLoadingTemplates,
    isInsertingTemplate,
    showTokenReplacementWarning,
    setShowTokenReplacementWarning,
    insertTemplate
  } = useTemplates({
    setReplyText,
    selectedTemplateLoadState,
    setSelectedTemplateLoadState
  });

  // If canProceed is false, texting is blocked. However, do NOT disable the
  // ChannelValueSelector when user phone numbers exist so the user can select
  // a different sending number that may be valid.
  const hasUserPhoneNumbers =
    dealerCommunicationDetails?.userPhoneNumbers.some(p => p.phoneNumber) ??
    false;

  const {
    menuItems: plusButtonMenuItems,
    isTemplateSelectorOpen,
    setIsTemplateSelectorOpen,
    isMediaLibraryOpen,
    setIsMediaLibraryOpen
  } = usePlusButtonMenuItems({
    autoLeadId,
    isEmailConversation: false,
    showMediaItem: showMediaLibraryFlag && isMediaEntitled
  });

  const editorRef = useRef<HTMLTextAreaElement>(null);

  const { insertContent } = useInsertAtCursor({
    replyText,
    setReplyText,
    editorRef
  });

  const handleTextMediaInsert = useCallback(
    (items: MediaEndlinkInsert[]) => {
      const newEndlinks: PendingEndlink[] = items.map(item => {
        const primary = item.media[0];
        return {
          chipId: makeChipId(),
          mediaId: primary?.id ?? "",
          mediaViewTrackingId: item.mediaViewTrackingId ?? "",
          title: primary?.title ?? "",
          endLink: item.endlinkUrl,
          thumbnail: primary?.thumbnailUrl
        };
      });
      setPendingEndlinks(prev => [...prev, ...newEndlinks]);
    },
    [setPendingEndlinks]
  );

  const handleMediaLibraryClose = useCallback(
    () => setIsMediaLibraryOpen(false),
    [setIsMediaLibraryOpen]
  );

  const {
    handleMediaSelect,
    isInserting: isMediaInserting,
    endlinkError,
    clearEndlinkError
  } = useMediaLibraryPicker({
    selectedMedia,
    setSelectedMedia,
    onInsertContent: handleTextMediaInsert,
    onComplete: handleMediaLibraryClose
  });

  const handleEmojiSelect = (emoji: string) => {
    insertContent(emoji);
  };

  const handleTemplateSelect = async (
    templateId: number,
    isGenAITemplate: boolean
  ) => {
    const success = await insertTemplate(templateId, isGenAITemplate);

    if (success) {
      setIsTemplateSelectorOpen(false);
    }
  };

  return (
    <>
      <ReplyAlertsTopContainer />
      <ChannelValueSelector
        isDisabled={
          !selectedReplyOption ||
          isTextWithRepairOrderOnly ||
          (isPhoneValidationBlocking && !hasUserPhoneNumbers)
        }
      />
      <StyledReplyBottom>
        <MessageInput
          isDisabled={
            !selectedReplyOption ||
            messageFailedToSend ||
            isTextOptInPending ||
            isTextAndNoPhoneNumber ||
            isTextWithRepairOrderOnly ||
            isPhoneValidationBlocking
          }
          stationeryOptions={stationeryOptions}
          textAreaRef={editorRef}
        />
      </StyledReplyBottom>
      {pendingEndlinks.length > 0 && (
        <StyledChipRail data-testid="text-pending-endlinks-rail">
          {pendingEndlinks.map(endlink => (
            <LinkPreviewCard
              data-testid={`pending-endlink-chip-${endlink.chipId}`}
              endLink={endlink.endLink}
              key={endlink.chipId}
              thumbnail={endlink.thumbnail}
              title={endlink.title}
              onDismiss={() => removeEndlink(endlink.chipId)}
            />
          ))}
        </StyledChipRail>
      )}
      <div>
        <MessageAttachmentUpload
          isDisabled={
            !selectedReplyOption ||
            messageFailedToSend ||
            isTextOptInPending ||
            isTextWithRepairOrderOnly ||
            isPhoneValidationBlocking
          }
        />
        <ReplyAlertsBottomContainer
          autoLeadId={autoLeadId}
          autoLeadStatus={autoLeadStatus}
          conversationType={ConversationTypeEnum.TEXT}
          emojiPickerError={emojiPickerError}
          endlinkError={endlinkError}
          isTextOptInPending={isTextOptInPending}
          leadStatusError={leadStatusError}
          messageFailedToSend={messageFailedToSend}
          messageSentResponseFailedMsg={messageSentResponseFailedMsg}
          repairOrderId={repairOrderId}
          selectedReplyOption={selectedReplyOption}
          showTokenReplacementWarning={showTokenReplacementWarning}
          onCloseTokenWarning={() => setShowTokenReplacementWarning(false)}
          onDismissEndlinkError={clearEndlinkError}
        />
        <StyledButtonContainer>
          <StyledLeftButtonGroup>
            {showPlusButton && (
              <PlusButton
                data-testid="reply-plus-button"
                disabled={
                  !selectedReplyOption ||
                  messageFailedToSend ||
                  isTextOptInPending ||
                  isTextWithRepairOrderOnly ||
                  isPhoneValidationBlocking
                }
                menuItems={plusButtonMenuItems}
              />
            )}
            {showTextEmojiPicker && (
              <TextMessageEmojiButton
                data-testid="text-message-emoji-picker"
                disabled={
                  !selectedReplyOption ||
                  messageFailedToSend ||
                  isTextOptInPending ||
                  isTextWithRepairOrderOnly ||
                  isPhoneValidationBlocking
                }
                onEmojiSelect={handleEmojiSelect}
              />
            )}
          </StyledLeftButtonGroup>
          <div style={{ marginLeft: "auto", display: "flex", gap: "0" }}>
            <MessageSendButton
              isTextAndNoPhoneNumber={isTextAndNoPhoneNumber}
              isTextWithRepairOrderOnly={isTextWithRepairOrderOnly}
              onMessageSendFailed={onMessageSendFailed}
              onMessageSendFailedSetMsg={onMessageSendFailedSetMsg}
            />
          </div>
        </StyledButtonContainer>
      </div>

      {/* Template selector modal */}
      <TemplateSelectorModal
        isInsertingTemplate={isInsertingTemplate}
        isLoading={isLoadingTemplates}
        isOpen={isTemplateSelectorOpen}
        templateData={templateData}
        onClose={() => setIsTemplateSelectorOpen(false)}
        onTemplateSelect={handleTemplateSelect}
      />

      {/* Media library modal */}
      <MediaLibraryModal
        isInserting={isMediaInserting}
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onMediaSelect={handleMediaSelect}
      />
    </>
  );
}

export default TextReply;
